import React, { FunctionComponent, useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Paper,
  Grid,
  Alert,
  InputAdornment,
  IconButton,
  Typography,
  Collapse,
} from '@mui/material';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import { useNavigate } from 'react-router-dom';
import { IMAGES } from '../../utils/constants';
import { ACTIVE_COLOR, INACTIVE_COLOR } from '../../components/ui/formInput';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useUpdateUserPassword } from '../../hooks/api/user';
import StorageService from '../../services/storageService';
import { useDispatch } from 'react-redux';
import { setLogout } from '../../store/slices/auth';
import { useQueryClient } from '@tanstack/react-query';

type ValidateField = {
  invalid: boolean;
  error: string;
};

type ValidateFieldsState = {
  currentPassword: ValidateField;
  newPassword: ValidateField;
  confirmPassword: ValidateField;
};

const ChangeTempPassword: FunctionComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const userInfo = useSelector((state: RootState) => state.auth.user);
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>();
  const [ disabled, setDisabled ] = useState<boolean>(false);
  const [isPasswordExpired, setIsPasswordExpired] = useState<boolean>(false);

  const formValidation: ValidateFieldsState = {
    currentPassword: { invalid: false, error: '' },
    newPassword: { invalid: false, error: '' },
    confirmPassword: { invalid: false, error: '' },
  };
  const { mutateAsync: changePassword } = useUpdateUserPassword(userInfo?.userId || '');
  const [validateFields, setValidateFields] = useState<ValidateFieldsState>(formValidation);

  // Check if this is a password expiry case
  useEffect(() => {
    if (userInfo?.passwordExpired) {
      setIsPasswordExpired(true);
    }
  }, [userInfo]);

  const isValidPassword = (password: string) => {
    // Check length
    if (password.length < 8) {
      return false;
    }
    
    // Check for at least one digit
    if (!/\d/.test(password)) {
      return false;
    }
    
    // Check for at least one uppercase letter
    if (!/[A-Z]/.test(password)) {
      return false;
    }
    
    // Check for at least one lowercase letter
    if (!/[a-z]/.test(password)) {
      return false;
    }
    
    // Check for at least one special character
    if (!/[!@#$%^&*()_+\-=\[\]{}|\\:;"'<>,.?/]/.test(password)) {
      return false;
    }
    
    return true;
  };

  const validateForm = () => {
    let valid = true;
    const errors = { ...validateFields };

    if (!currentPassword.trim()) {
      errors.currentPassword = {
        invalid: true,
        error: 'Current Password is required',
      };
      valid = false;
    } else if (!isValidPassword(currentPassword)) {
      errors.currentPassword = {
        invalid: true,
        error: 'Password must meet all requirements.',
      };
      valid = false;
    } else {
      errors.currentPassword = { invalid: false, error: '' };
    }

    if (!newPassword.trim()) {
      errors.newPassword = {
        invalid: true,
        error: 'New Password is required',
      };
      valid = false;
    } else if (!isValidPassword(newPassword)) {
      errors.newPassword = {
        invalid: true,
        error: 'Password must meet all requirements.',
      };
      valid = false;
    } else if (newPassword.trim() === currentPassword.trim()) {
      errors.newPassword = {
        invalid: true,
        error: 'New password must be different from current password.',
      };
      valid = false;
    } else {
      errors.newPassword = { invalid: false, error: '' };
    }

    if (!confirmPassword.trim()) {
      errors.confirmPassword = {
        invalid: true,
        error: 'Confirm Password is required',
      };
      valid = false;
    } else if (confirmPassword.trim() !== newPassword.trim()) {
      errors.confirmPassword = {
        invalid: true,
        error: 'Confirm Passwords do not match',
      };
      valid = false;
    } else {
      errors.confirmPassword = { invalid: false, error: '' };
    }

    setValidateFields(errors);
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setValidateFields(formValidation);
    
    // Guard clause: Check if userInfo exists
    if (!userInfo?.userId) {
      setError('User information not found. Please try logging in again.');
      setTimeout(() => {
        setError('');
        navigate('/login');
      }, 3000);
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setDisabled(true);
      const result = await changePassword({
        currentPassword: currentPassword,
        newPassword: newPassword,
        confirmPassword: confirmPassword
      });
      if (result?.status === 200) {
        setSuccess(true);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        
        // Add this line to clear the flag
        StorageService.setForceTempPassword("");
        StorageService.clearTokens();
        
        // Set flag indicating recent password change
        StorageService.setRecentPasswordChange("true");
        
        // Clear React Query cache
        queryClient.clear();
        dispatch(setLogout());
        navigate('/login');  // Change from '/dashboard' to '/login'
      } else {
        setDisabled(false);
        setError('An error occurred during login. Please try again.');
        setTimeout(() => {
          setError('');
        }, 3000);
      }
    } catch (err: any) {
      setDisabled(false);
      if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError('An error occurred during login. Please try again.');
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: '#c3183c',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          width: 1000,
          minHeight: 520,
          overflow: 'visible',
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            width: '50%',
            backgroundImage: `linear-gradient(rgba(255, 0, 64, 0.4), rgba(255, 0, 64, 0.4)), url(${IMAGES.bannerImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />

        <Box
          sx={{
            width: '50%',
            bgcolor: '#f9f9f9',
            px: 6,
            py: 5,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 4 }}
          >
            <Grid>
              <img
                src={IMAGES.barksdaleLogo}
                alt="Barksdale Logo"
                style={{ height: 50 }}
              />
            </Grid>
            <Grid>
              <img
                src={IMAGES.extranetLogo}
                alt="Extranet Logo"
                style={{ height: 50 }}
              />
            </Grid>
          </Grid>

          {isPasswordExpired && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Your password has expired and must be changed before you can continue.
            </Alert>
          )}

          <TextField
            fullWidth
            label="Current Password"
            variant="outlined"
            margin="normal"
            type={showCurrentPassword ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            error={validateFields.currentPassword.invalid}
            helperText={validateFields.currentPassword.error}
            FormHelperTextProps={{ sx: { marginLeft: '0px' } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onMouseEnter={() => setShowCurrentPassword(true)}
                    onMouseLeave={() => setShowCurrentPassword(false)}
                    edge="end"
                  >
                    {showCurrentPassword ? <LockOpenOutlinedIcon /> : <LockOutlinedIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{
              sx: {
                color: currentPassword ? ACTIVE_COLOR : INACTIVE_COLOR,
                "&.Mui-focused": {
                  color: ACTIVE_COLOR,
                },
                "&.MuiInputLabel-shrink": {
                  color: ACTIVE_COLOR,
                },
                "&:hover": {
                  color: ACTIVE_COLOR,
                },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: ACTIVE_COLOR,
                },
                "&:hover fieldset": {
                  borderColor: ACTIVE_COLOR,
                },
              },
            }}
          />
          <TextField
            fullWidth
            label="New Password"
            variant="outlined"
            margin="normal"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={validateFields.newPassword.invalid}
            helperText={validateFields.newPassword.error}
            FormHelperTextProps={{ sx: { marginLeft: '0px' } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onMouseEnter={() => setShowNewPassword(true)}
                    onMouseLeave={() => setShowNewPassword(false)}
                    edge="end"
                  >
                    {showNewPassword ? <LockOpenOutlinedIcon /> : <LockOutlinedIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{
              sx: {
                color: newPassword ? ACTIVE_COLOR : INACTIVE_COLOR,
                "&.Mui-focused": {
                  color: ACTIVE_COLOR,
                },
                "&.MuiInputLabel-shrink": {
                  color: ACTIVE_COLOR,
                },
                "&:hover": {
                  color: ACTIVE_COLOR,
                },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: ACTIVE_COLOR,
                },
                "&:hover fieldset": {
                  borderColor: ACTIVE_COLOR,
                },
              },
            }}
          />

          {/* Password Requirements Hint */}
          <Box sx={{ mt: 0, mb: 0, p: 0, backgroundColor: '#ffffff', borderRadius: 1, border: '1px solid #ffffff' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.875rem', 
                color: '#333', 
                cursor: 'pointer',
                textDecoration: 'underline',
                fontWeight: '500',
                '&:hover': { color: '#c3183c' }
              }}
              onClick={() => setShowPasswordRequirements(!showPasswordRequirements)}
            >
              Password must meet requirements (click to view)
            </Typography>
            
            <Collapse in={showPasswordRequirements}>
              <Box sx={{ mt: 1, pl: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#666', mb: 0.5 }}>
                  • Minimum 8 characters
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#666', mb: 0.5 }}>
                  • At least one uppercase letter (A-Z)
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#666', mb: 0.5 }}>
                  • At least one lowercase letter (a-z)
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#666', mb: 0.5 }}>
                  • At least one digit (0-9)
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#666', mb: 0.5 }}>
                  • At least one special character: !@#$%^&*()_+-=[]{}|\:;"'&lt;,&gt;.?/
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#666' }}>
                  • No spaces allowed
                </Typography>
              </Box>
            </Collapse>
          </Box>

          <TextField
            fullWidth
            label="Confirm Password"
            variant="outlined"
            margin="normal"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={validateFields.confirmPassword.invalid}
            helperText={validateFields.confirmPassword.error}
            FormHelperTextProps={{ sx: { marginLeft: '0px' } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onMouseEnter={() => setShowConfirmPassword(true)}
                    onMouseLeave={() => setShowConfirmPassword(false)}
                    edge="end"
                  >
                    {showCurrentPassword ? <LockOpenOutlinedIcon /> : <LockOutlinedIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{
              sx: {
                color: confirmPassword ? ACTIVE_COLOR : INACTIVE_COLOR,
                "&.Mui-focused": {
                  color: ACTIVE_COLOR,
                },
                "&.MuiInputLabel-shrink": {
                  color: ACTIVE_COLOR,
                },
                "&:hover": {
                  color: ACTIVE_COLOR,
                },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: ACTIVE_COLOR,
                },
                "&:hover fieldset": {
                  borderColor: ACTIVE_COLOR,
                },
              },
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {
            success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Password changed successfully!
              </Alert>
            )
          }

          <Box display="flex" gap={2} mt={2} mb={2}>
            <Button
              disabled={disabled} 
              variant="contained"
              fullWidth
              sx={{
                backgroundColor: '#c3183c',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#a31534',
                },
              }}
              onClick={handleSubmit}
            >
              SUBMIT
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ChangeTempPassword;
